import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { captureError } from '@clatter/platform';
import { SlideItem } from './SlideItem';

import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

import { RootState } from '../../../store';
import { getLongPullUIState } from '../../../store/UI/longPull.slice';
import type { SlideItemCompatibilityBadge } from './SlideItem';

interface Props {
  dialogState: FolderManagerSlidesDialogState;
  onDismiss: (data: FolderManagerSlidesDialogState | null, shouldRefetch?: boolean) => void;
}

interface Slide {
  slide_number: number;
  thumbnail_base64: string;
  slide_title?: string;
  layout_name?: string | null;
  layout_in_all_themes?: boolean;
  matched_theme_names?: string[];
  not_matched_theme_names?: string[];
}

interface ApiResponse {
  data?: Slide[];
  status?: string;
  Status?: string;
}

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  gap: 20px;
  padding: 20px;
  width: 100%;
`;

const getSlidesApiResponse = (responseData: unknown): ApiResponse | null => {
  if (!responseData) {
    return null;
  }

  try {
    const data = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;

    if ((data as any)?.Slides) {
      return { data: (data as any).Slides, status: (data as any).Status };
    }

    return { data: Array.isArray(data) ? data : [] };
  } catch (error) {
    captureError(error, { message: 'Failed to parse API response:', component: 'FolderManagerSlidesDialog' });
    return null;
  }
};

const getSlideCompatibilityBadge = (
  slide: Slide,
): SlideItemCompatibilityBadge | undefined => {
  const isDeckIngest = Boolean(slide.matched_theme_names && slide.not_matched_theme_names);

  if (!isDeckIngest || !slide.layout_name) {
    return undefined;
  }

  return {
    layoutName: slide.layout_name,
    supportLevel: slide.layout_in_all_themes ? 'FULLY_SUPPORTED' : 'NOT_SUPPORTED',
    successTooltipTitle: 'All themes support this layout',
    errorTooltipTitle: slide.not_matched_theme_names?.length ? (
      <div>
        <div>Not supported by:</div>
        {slide.not_matched_theme_names.map((name) => (
          <div key={name}>• {name}</div>
        ))}
      </div>
    ) : undefined,
  };
};

export const FolderManagerSlidesDialog: FC<Props> = ({ dialogState, onDismiss }) => {
  const uploadItems = useSelector((state: RootState) => {
    const longPullState = getLongPullUIState(state);
    return longPullState?.['global'] || [];
  });
  const uploadStatus = uploadItems.find((item: any) => item.id === dialogState.jobId);

  const apiResponseData = useMemo(() => getSlidesApiResponse(uploadStatus?.responseData), [uploadStatus?.responseData]);

  const slides = apiResponseData?.data || [];
  const apiStatus = uploadStatus?.apiStatus?.toLowerCase?.() || '';
  const isCompleted = uploadStatus?.isCompleted || ['success', 'failed', 'timeout'].includes(apiStatus);

  const handleDismiss = () => {
    onDismiss(dialogState, isCompleted);
  };

  return (
    <Dialog fullScreen open={dialogState.opened} onClose={handleDismiss} sx={{ zIndex: 99999 }}>
      <AppBar sx={{ position: 'relative', background: 'white' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleDismiss} aria-label="close">
            <CloseIcon style={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }} style={{ color: 'black' }}>
            Processing {dialogState.title} {slides.length > 0 && `(${slides.length})`}
          </Typography>
        </Toolbar>
      </AppBar>

      <div style={{ padding: 20, overflowY: 'auto' }}>
        {!uploadStatus && <p>Initializing upload...</p>}

        {uploadStatus && (
          <>
            <SlidesGrid>
              {slides.length > 0 ? (
                slides.map((slide) => (
                  <SlideItem
                    key={slide.slide_number}
                    slideNumber={slide.slide_number}
                    thumbnailBase64={slide.thumbnail_base64}
                    slideTitle={slide.slide_title}
                    compatibilityBadge={getSlideCompatibilityBadge(slide)}
                  />
                ))
              ) : (
                <p>Waiting for slides data...</p>
              )}
            </SlidesGrid>
          </>
        )}
      </div>
    </Dialog>
  );
};
